import {
  DiseaseCategoryType,
  NextStepSuggestedFeatures,
  QualifiedFeatureFragment,
} from "fe-common/graphql";

type DDClinicalReasoning = any;
type DdNextStep = any;

import { useEffect, useState } from "react";

export enum NextStepAnswer {
  has = "has",
  hasnot = "hasNot",
  unknown = "unknown",
  skip = "skip",
}

export interface NextStepResponse {
  qf: QualifiedFeatureFragment;
  answer: NextStepAnswer;
  score: number;
  ddScores: { qf: QualifiedFeatureFragment; score: number }[];
}

export interface NextStepCard {
  id: string;
}

export function isCardSingle(c: NextStepCard): c is NextStepCardSingle {
  return (c as NextStepCardSingle)?.suggestion != null;
}

export function isCardGroup(c: NextStepCard): c is NextStepCardGroup {
  return (c as NextStepCardGroup)?.children != null;
}

export interface NextStepCardGroup extends NextStepCard {
  id: string;
  title: QualifiedFeatureFragment;
  children: NextStepResponse[];
}

export interface NextStepCardSingle {
  id: string;
  suggestion: NextStepResponse;
}

export enum NextStepCategory {
  Demographic = "Demographic",
  Clinical = "Clinical",
  Diagnostic = "Diagnostic",
}
export function nextStepCategory(t: DiseaseCategoryType) {
  switch (t) {
    case DiseaseCategoryType.OutcomeSigns:
      return NextStepCategory.Clinical;
    case DiseaseCategoryType.Imaging:
    case DiseaseCategoryType.OutcomeTest:
      return NextStepCategory.Diagnostic;
    default:
      return NextStepCategory.Demographic;
  }
}

export const tabIcon = {
  [NextStepCategory.Diagnostic]: "fas fa-vial",
  [NextStepCategory.Demographic]: "fas fa-male",
  [NextStepCategory.Clinical]: "fas fa-stethoscope",
};

function categorizeAndFilterSuggestions(
  data: DdNextStep,
  answers: Map<string, NextStepResponse>,
): Partial<Record<NextStepCategory, NextStepSuggestedFeatures[]>> {
  let suggestions: Partial<Record<
    NextStepCategory,
    NextStepSuggestedFeatures[]
  >> = {};

  if (data || data.suggestedFeatures) {
    data.suggestedFeatures.forEach((s) => {
      if (!answers.has(s.qualifiedFeature.id)) {
        let c = nextStepCategory(s.category);
        if (!suggestions[c]) suggestions[c] = [];
        if (c) suggestions[c].push(s);
      }
    });
  }
  return suggestions;
}

type NextStepResponseMap = Map<string, NextStepResponse>;

const CARD_CHILD_LIMIT = 5;

function buildCards(
  suggestions: NextStepSuggestedFeatures[],
  selections: NextStepResponseMap,
  dd: QualifiedFeatureFragment[],
): NextStepCard[] {
  // map cards to base feature
  let cards: NextStepCard[] = [];
  let featureToIndex = new Map<string, number>();
  let makeResponse = (sg: NextStepSuggestedFeatures) =>
    ({
      qf: sg.qualifiedFeature,
      answer:
        selections.get(sg.qualifiedFeature.id)?.answer ||
        NextStepAnswer.unknown,
      score: sg.sumLiftScores,
      ddScores: sg.liftScores
        ?.map((ls) => ({
          qf: dd && dd.find((d) => d.id == ls.qualifiedFeature.id),
          score: ls.liftScore,
        }))
        .filter((dds) => dds.qf != null),
    } as NextStepResponse);
  let idIndex = 1;
  for (let sg of suggestions) {
    let qf = sg.qualifiedFeature;
    let featureid = qf.feature.id;
    let index = featureToIndex.get(featureid);
    let existingCard = index != undefined && cards[index];
    if (
      isCardGroup(existingCard) &&
      existingCard.children.length >= CARD_CHILD_LIMIT
    ) {
      existingCard = null;
    }
    if (existingCard) {
      // handle existing entry...
      let existingCard = cards[index];
      let groupCard: NextStepCardGroup = isCardSingle(existingCard)
        ? {
            id: `${featureid}_${idIndex++}`,
            title: {
              ...existingCard.suggestion.qf,
              qualifiers: [],
              mandatory: null,
            },
            children: [existingCard.suggestion],
          }
        : (existingCard as NextStepCardGroup);
      groupCard.children.push(makeResponse(sg));
      cards[index] = groupCard;
    } else {
      // new entry
      let card: NextStepCardSingle = {
        id: featureid,
        suggestion: makeResponse(sg),
      };
      featureToIndex.set(featureid, cards.push(card) - 1);
    }
  }
  return cards;
}

export function useNextStepData(
  data: DdNextStep,
  clinicalReasoning?: DDClinicalReasoning[],
) {
  let [answered, setAnswered] = useState<NextStepResponseMap>(new Map());
  let [suggestions, setSuggestions] = useState<
    Partial<Record<NextStepCategory, NextStepSuggestedFeatures[]>>
  >(null);
  let [selections, setSelections] = useState<NextStepResponseMap>(new Map());
  useEffect(() => {
    // build suggestions
    if (data) setSuggestions(categorizeAndFilterSuggestions(data, answered));
    else setSuggestions(null);
  }, [data, data?.suggestedFeatures, answered]);

  // combine suggestions with current selections into cards
  let currentAnswers = {};
  if (suggestions)
    Object.keys(suggestions).forEach((c) => {
      currentAnswers[c] = buildCards(
        suggestions[c],
        selections,
        clinicalReasoning && clinicalReasoning.map((cr) => cr.disease[0]),
      );
    });
  // callback when reloading dd
  let submit = () => {
    // add current to answered...

    let all = new Map([...answered, ...selections]);
    setSelections(new Map());
    setAnswered(all);
    return [...all.values()].map(answerToQFF).filter((f) => f != null);
  };

  return {
    nextSteps: currentAnswers,
    setAnswer: (a: NextStepResponse) => (answer) => {
      if (answer == NextStepAnswer.skip) {
        answered.set(a.qf.id, { ...a, answer });
        setAnswered(new Map(answered));
      } else {
        selections.set(a.qf.id, {
          ...a,
          answer: answer != a.answer ? answer : NextStepAnswer.unknown,
        });
        setSelections(new Map(selections));
      }
    },
    hasAnswers: selections && selections.size > 0,
    submit: submit,
  };
}

function answerToQFF(response: NextStepResponse) {
  // simplistic...needs work
  if (response.answer == NextStepAnswer.has) {
    return response.qf;
  } else if (response.answer == NextStepAnswer.hasnot) {
    return { ...response.qf, negate: !response.qf.negate };
  } else return null;
}
